/**
 * Utility functions for permission checking
 */

import { useAuth } from '@/context/AuthContext';

/**
 * Check if user has a specific permission
 * @param userPermissions Array of permission names the user has
 * @param permissionName The permission to check (e.g., 'shift_types.create')
 * @returns boolean
 */
export const hasPermission = (userPermissions: string[], permissionName: string): boolean => {
  return userPermissions.includes(permissionName);
};

/**
 * Check if user can perform an action on a feature
 * @param userPermissions Array of permission names the user has
 * @param feature The feature name (e.g., 'shift_types')
 * @param action The action name (e.g., 'create')
 * @returns boolean
 */
export const canPerform = (userPermissions: string[], feature: string, action: string): boolean => {
  return hasPermission(userPermissions, `${feature}.${action}`);
};

/**
 * Check if user is admin
 * @param userRole The user's role
 * @returns boolean
 */
export const isAdmin = (userRole: string): boolean => {
  return userRole === 'admin';
};

/**
 * Check if user is manager or admin
 * @param userRole The user's role
 * @returns boolean
 */
export const isManagerOrAdmin = (userRole: string): boolean => {
  return userRole === 'admin' || userRole === 'manager';
};

/**
 * Get permission display name
 * @param permissionName The permission name (e.g., 'shift_types.create')
 * @returns Formatted display name
 */
export const getPermissionDisplayName = (permissionName: string): string => {
  const [feature, action] = permissionName.split('.');
  const featureDisplay = feature.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
  const actionDisplay = action.charAt(0).toUpperCase() + action.slice(1);
  return `${actionDisplay} ${featureDisplay}`;
};

/**
 * Group permissions by feature
 * @param permissions Array of permission objects
 * @returns Object with feature as key and permissions as value
 */
export const groupPermissionsByFeature = (permissions: any[]) => {
  return permissions.reduce((acc, permission) => {
    if (!acc[permission.feature]) {
      acc[permission.feature] = [];
    }
    acc[permission.feature].push(permission);
    return acc;
  }, {} as Record<string, any[]>);
};

/**
 * React hook for permission checking
 * @returns Object with permission checking functions
 */
export const usePermissions = () => {
  const { permissions, user } = useAuth();

  const hasPermission = (permissionName: string): boolean => {
    return permissions.includes(permissionName);
  };

  const canPerform = (feature: string, action: string): boolean => {
    return hasPermission(`${feature}.${action}`);
  };

  const isAdmin = (): boolean => {
    return user?.role === 'admin';
  };

  const isManagerOrAdmin = (): boolean => {
    return user?.role === 'admin' || user?.role === 'manager';
  };

  return {
    hasPermission,
    canPerform,
    isAdmin,
    isManagerOrAdmin,
    permissions
  };
}; 